import {ComponentChildren, createRef, RefObject} from "preact";
import {signal, Signal} from "@preact/signals";
import styled from "styled-components";
import {IconButton} from "./button";
import {mdiClose} from "@mdi/js";
import {useEffect, useState} from "preact/hooks";
import {ForwardedRef, forwardRef} from "react";
import {AccentType, getAccentStyle, withAccent} from "../../theme/accents";

type Flash = {
    children?: ComponentChildren;
    timeout?: number;
    closable?: boolean;
    className?: string;
    target?: RefObject<HTMLDivElement>;
    accent?: AccentType;
};

type ID = {
    _id: number;
};

let _lastFlashId = 0;
let _globalContainerId = 0;

const flashes: {[key: number]: Signal<(Flash & ID)[]>} = {};
const globalFlashTarget: RefObject<HTMLDivElement> = createRef();

function removeFlash(id: number) {
    for (const scopedFlashes of Object.values(flashes)) {
        scopedFlashes.value = scopedFlashes.value.filter(flash => flash._id !== id);
    }
}

function showFlash(props: Flash): number {
    (props as Flash & ID)._id = ++_lastFlashId;

    const target = props.target ?? globalFlashTarget;

    if (!target.current) {
        if (props.target) {
            throw new Error("Specified flash target does not exists.")
        } else {
            throw new Error("<GlobalFlashTarget /> missing. To use global flashes, use this component in your application.");
        }
    }

    const match = target.current.id.match(/^_flash_target_([0-9]+)$/)
    if (!match) {
        throw new Error("Specified flash target is not valid <FlashTarget> component.");
    }

    const targetId = Number.parseInt(match[1]);

    flashes[targetId].value = [...flashes[targetId].value, props as Flash & ID];
    return (props as Flash & ID)._id;
}

const FlashMessage = withAccent(styled((props: Flash & ID) => {
    useEffect(() => {
        if (!props.timeout) {
            return;
        }

        const handle = setTimeout(() => {
            removeFlash(props._id);
        }, props.timeout)
        return () => {
            clearTimeout(handle);
        }
    }, [props.timeout]);

    return <div className={props.className}>
        <div>{props.children}</div>
        {props.closable && <IconButton path={mdiClose} onClick={() => {
            removeFlash(props._id);
        }} />}
    </div>
})`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    pointer-events: all;
    
    background: ${props => getAccentStyle(props).cardBg};
    border: solid 1px ${props => getAccentStyle(props).primaryBg};
    box-shadow: 0.2rem 0.2rem 0.5rem rgba(0, 0, 0, 0.6);
    padding: 0.5rem;
    color: ${props => getAccentStyle(props).cardFg};
    text-shadow: 0.1rem 0.1rem 0.1rem rgba(0, 0, 0, 0.12);
    box-sizing: border-box;
    
    > ${IconButton} {
        background: none;
        padding: .2rem;
        color: ${props => getAccentStyle(props).cardFg};
        line-height: .7rem;
        margin-left: 1rem;
        
        &:hover {
            background: ${props => props.theme.colors.hover(getAccentStyle(props).cardBg)};
        }
    }
`, "info");

const FlashContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1rem;
`;

export const FlashTarget = styled(forwardRef((props: {className?: string}, forwardedRef: ForwardedRef<HTMLDivElement>) => {
    const [id] = useState<number>(() => ++_globalContainerId);
    if (!Object.prototype.hasOwnProperty.call(flashes, id)) {
        flashes[id] = signal([]);
    }

    return <div className={props.className} ref={forwardedRef} id={`_flash_target_${id}`}>
        <FlashContainer>{flashes[id].value.map(flash =>
            <FlashMessage {...flash} />
        )}</FlashContainer>
    </div>
}))`
    width: 100%;
    display: flex;
    justify-content: center;
    pointer-events: none;
`;

export const GlobalFlashTarget = styled((props: {className?: string}) => {
    return <FlashTarget ref={globalFlashTarget} {...props} />;
})`
    z-index: 999;
    position: fixed;
    top: 3rem;
    left: 0;
    
    > div {
        min-width: 15rem;
        max-width: 90%;
    }
`;

export function useFlash() {
    return {
        flash: (text: ComponentChildren, opts: Omit<Flash, "children" | "closable"> = {}) => showFlash({
            children: text,
            closable: true,
            ...opts
        }),

        autoFlash: (text: ComponentChildren, timeout: number = 5000, {closable, ...opts}: Omit<Flash, "children" | "timeout"> = {}) => showFlash({
            children: text,
            timeout: timeout,
            closable: closable ?? true,
            ...opts
        }),

        customFlash: (text: ComponentChildren, opts: Omit<Flash, "children"> = {}) => showFlash({
            children: text,
            ...opts
        }),

        success: (text: ComponentChildren, opts: Omit<Flash, "children"> = {}) => showFlash(Object.assign({
            children: text,
            timeout: 5000,
            closable: true,
            accent: "success"
        }, opts)),

        hide: (flash: number) => removeFlash(flash),
    };
}
